/* eslint max-lines: ["error", { max: 592 }] -- applies to entire file */
import { createSelector } from "@reduxjs/toolkit";
import {
  differenceInDays,
  eachDayOfInterval,
  format,
  isSameDay,
  max,
  parse,
  parseISO,
  subDays,
} from "date-fns";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import {
  SMTEntryType,
  SMTPermissionTypeValue,
  SMTWeekLogLayout,
} from "@/constants/SMT";
import { UnencryptedStorageKey } from "@/hooks/useUnencryptedStorage";
import type {
  GetSMTDailyLogsPageParam,
  SMTSummaryEntry,
} from "@/store/api/smt";
import type { AllSMTDailyEntriesModalProps } from "@/ui/components/smt/ViewSMTEntries/AllSMTDailyEntriesModal";
import type {
  SMTEntryDetails,
  SMTEntryProps,
} from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import { formatTime } from "@/utils/date.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { getPreferredFullName } from "@/utils/user.util";

const StartDateOffset = 6;

// Initial date range offset to fetch, with infinite scroll to fetch subsequent date ranges
// NOTE: kept to a very low number to reduce load on backend API since SMTDailyLogEntry objects are very large
const AllEntriesStartDateOffset = 14;

const canAddMeals = (permission: SMTPermissionType): boolean => {
  return permission === SMTPermissionTypeValue.Add;
};

const isViewable = (permission: SMTPermissionType): boolean => {
  // anyone with 'Add' permission can also 'View'
  return permission === SMTPermissionTypeValue.View || canAddMeals(permission);
};

// Generate a storage key that is unique to the user because we want to track interaction per user
const getUserInteractionStorageKey = (userId: string): string =>
  `${UnencryptedStorageKey.HasInteractedWithSMTFeature}_${userId}`;

type SMTWeekDay = {
  date: Date;
  displayDate: string;
  hasLogs: boolean;
  isPartOfStreak: boolean;
};

type SMTWeekStreak = {
  endIndex: number;
  startIndex: number;
};

type SMTWeekData = {
  streaks: SMTWeekStreak[];
  weekDays: SMTWeekDay[];
};

const hasLogForDate = ({
  date,
  datesWithLogs,
}: {
  date: Date;
  datesWithLogs?: Date[];
}) => {
  if (!isNotNilNotEmpty(datesWithLogs)) {
    return false;
  }

  return datesWithLogs.some((logDate) => isSameDay(logDate, date));
};

const identifyStreaks = (weekDays: SMTWeekDay[]): SMTWeekStreak[] => {
  const streaks: SMTWeekStreak[] = [];
  let streakStart: number | undefined;

  // eslint-disable-next-line max-params
  weekDays.forEach((day, index) => {
    if (day.hasLogs) {
      if (isNil(streakStart)) {
        streakStart = index;
      }
    } else if (!isNil(streakStart)) {
      if (index - streakStart > 1) {
        streaks.push({ endIndex: index - 1, startIndex: streakStart });
      }
      streakStart = undefined;
    }
  });

  if (!isNil(streakStart) && weekDays.length - streakStart > 1) {
    streaks.push({ endIndex: weekDays.length - 1, startIndex: streakStart });
  }

  return streaks;
};

const generateWeekDays = (datesWithLogs?: Date[]): SMTWeekDay[] => {
  const today = new Date();

  const weekdays = Array.from({ length: SMTWeekLogLayout.NumberOfDays }).map(
    // eslint-disable-next-line max-params
    (_, index) => {
      const date = subDays(today, index);
      const hasLogs = hasLogForDate({ date, datesWithLogs });
      const displayDate = format(date, DateFormat.shortWeekday);

      return {
        date,
        displayDate,
        hasLogs,
        isPartOfStreak: false,
      };
    },
  );

  return weekdays.reverse();
};

const generateWeekData = (datesWithLogs?: Date[]): SMTWeekData => {
  const weekDays = generateWeekDays(datesWithLogs);
  const streaks = identifyStreaks(weekDays);

  // Mark days that are part of a streak
  // eslint-disable-next-line max-params -- need index to check streak membership
  const weekDaysWithStreakInfo = weekDays.map((day, index) => {
    const isPartOfStreak = streaks.some(
      (streak) => index >= streak.startIndex && index <= streak.endIndex,
    );
    return {
      ...day,
      isPartOfStreak,
    };
  });

  return {
    streaks,
    weekDays: weekDaysWithStreakInfo,
  };
};

/**
 * Calculates the date range for the SMT week summary.
 * Returns startDate (6 days ago) and endDate (today) in YYYY-MM-DD format.
 * This matches the 7-day range displayed in the UI (today through 6 days ago, inclusive).
 *
 * @returns Object with startDate and endDate strings in YYYY-MM-DD format
 */
const getDateRangeForWeekSummary = (): {
  endDate: string;
  startDate: string;
} => {
  const today = new Date();
  const startDate = subDays(today, StartDateOffset);

  return {
    endDate: format(today, DateFormat.yearMonthDay),
    startDate: format(startDate, DateFormat.yearMonthDay),
  };
};

/**
 * Parses a date string in YYYY-MM-DD format into a Date object.
 *
 * @param date - A string representing a date in YYYY-MM-DD format
 * @returns A Date object representing the parsed date
 */
const parseDailyLogDate = (date: string) =>
  parse(date, DateFormat.yearMonthDay, new Date());

/**
 * Calculates the initial date range for the viewing all SMT Daily Logs.
 * Returns (in YYYY-MM-DD format):
 *    startDate (allEntriesStartDateOffset days ago)
 *      endDate (today)
 *
 * @returns Object with startDate and endDate strings in YYYY-MM-DD format
 */
const getInitialDateRangeForAllSMTDailyLogs = ({
  admitDate: admitDateString,
}: { admitDate?: string } = {}): GetSMTDailyLogsPageParam => {
  const today = new Date();
  const startDate = subDays(today, AllEntriesStartDateOffset);

  const admitDate = admitDateString && parseDailyLogDate(admitDateString);
  const maxStartDate = admitDate ? max([admitDate, startDate]) : startDate;
  return {
    endDate: format(today, DateFormat.yearMonthDay),
    startDate: format(maxStartDate, DateFormat.yearMonthDay),
  };
};

/**
 * Calculates the next page's date range for the viewing all SMT Daily Logs,
 * based on the size/offset of the current date range.
 * Returns (in YYYY-MM-DD format):
 *    startDate (oldStartDate - 1 - oldOffset)
 *      endDate (oldStartDate - 1)
 *
 * @param startDate - a string in YYYY-MM-DD format for the earlier date in the date range
 * @param endDate - a string in YYYY-MM-DD format for the later date in the date range
 * @returns Object with startDate and endDate strings in YYYY-MM-DD format
 */
const getNextDateRangeForAllSMTDailyLogs = ({
  admitDate: admitDateString,
  endDate: endDateString,
  startDate: startDateString,
}: GetSMTDailyLogsPageParam): GetSMTDailyLogsPageParam => {
  const oldEndDate = parseDailyLogDate(endDateString);
  const oldStartDate = parseDailyLogDate(startDateString);
  const oldOffset = differenceInDays(oldEndDate, oldStartDate);

  const admitDate = admitDateString && parseDailyLogDate(admitDateString);

  // [oldStart, oldEnd] becomes [oldStart -1 - oldOffset, oldStart -1] so no overlapping days
  // eg [2025-02-15, 2025-02-25] becomes [2025-02-04, 2025-02-14] (example offset of 10)
  const newEndDate = subDays(oldStartDate, 1);
  const newStartDate = subDays(newEndDate, oldOffset);
  const maxStartDate = admitDate
    ? max([admitDate, newStartDate])
    : newStartDate;
  return {
    endDate: format(newEndDate, DateFormat.yearMonthDay),
    startDate: format(maxStartDate, DateFormat.yearMonthDay),
  };
};

/**
 * Maps SMT summary entries to an array of Date objects
 * for dates that have entries.
 *
 * @param entries - Array of entries with date and hasEntry flags
 * @returns Array of Date objects for dates that have entries
 */
const mapSMTSummaryResponseToDates = (entries: SMTSummaryEntry[]): Date[] =>
  entries
    .filter((entry) => entry.hasEntry)
    .map((entry) => parse(entry.date, DateFormat.yearMonthDay, new Date()));

type SMTDailyLogEntryToSMTEntryDetailsPropsMapper = (
  smtDailyLogEntry: SMTDailyLogEntry,
) => SMTEntryDetails["entryDetailsProps"];

const mapMealSMTEntryTypeToSMTEntryDetailProps: SMTDailyLogEntryToSMTEntryDetailsPropsMapper =
  (
    smtDailyLogEntry: SMTDailyLogEntry,
  ): SMTEntryDetails["entryDetailsProps"] => {
    const {
      comment: foodComment,
      description: foodDescription,
      isNewFood,
      mealType: foodType,
    } = smtDailyLogEntry;
    return {
      foodComment,
      foodDescription,
      foodType,
      isNewFood,
    };
  };

const mapSMTDailyLogEntryToSMTEntryDetailProps = (
  smtDailyLogEntry: SMTDailyLogEntry,
): SMTEntryDetails["entryDetailsProps"] => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- must assume API returned a valid entryType here
  const entryType = smtDailyLogEntry.entryType.toLowerCase() as SMTEntryType;

  // using this Record mapper type means that, as SMTEntryType gets new values, this code will be required to be updated
  // for typescript to compile correctly; reducing the chances that handling a new SMTEntryType is overlooked here
  const entryTypeHandlers: Record<
    ValueOf<typeof SMTEntryType>,
    SMTDailyLogEntryToSMTEntryDetailsPropsMapper
  > = {
    [SMTEntryType.Meal]: mapMealSMTEntryTypeToSMTEntryDetailProps,
  };

  const relevantHandler = entryTypeHandlers[entryType];
  return relevantHandler(smtDailyLogEntry);
};

/**
 * Formats an SMT `occurredAt` timestamp into a time string for display.
 *
 * The SMT API returns `occurredAt` as an ISO-like timestamp (e.g., "2026-01-05T05:14:08.079").
 * This function parses the timestamp as-is without any timezone conversion and formats
 * it for display (e.g., "5:14 AM").
 *
 * If the timestamp cannot be parsed, an empty string is returned to avoid
 * masking API data issues with misleading fallback values.
 *
 * @param occurredAt - ISO-like timestamp string from the API.
 * @returns A formatted time string (e.g., "5:14 AM") or an empty string if the timestamp
 *   is invalid.
 */
const formatOccurredAtToLocalTime = (occurredAt: string): string => {
  // Parse the timestamp as-is without timezone conversion
  const occurredAtDate = parseISO(occurredAt);
  // Return empty string for invalid dates rather than misleading fallback
  return Number.isNaN(occurredAtDate.getTime())
    ? ""
    : formatTime({ date: occurredAtDate });
};

const mapSMTDailyLogEntryToSMTEntryProps = (
  smtDailyLogEntry: SMTDailyLogEntry,
): SMTEntryProps => {
  const {
    createdByUserFirstName,
    createdByUserId: avatarUserId,
    createdByUserLastName,
    createdByUserType,
    id: smtEntryId,
    images,
    occurredAt,
  } = smtDailyLogEntry;
  const firstName = createdByUserFirstName ?? "";
  const lastName = createdByUserLastName ?? "";
  const preferredName = getPreferredFullName({ firstName, lastName }).trim();
  const avatarUserName = isEmpty(preferredName) ? undefined : preferredName;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- must assume API returned a valid BaseUserType (or nothing)
  const avatarUserType = createdByUserType as BaseUserType | undefined;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- must assume API returned a valid entryType here
  const entryType = smtDailyLogEntry.entryType.toLowerCase() as SMTEntryType;
  const time = formatOccurredAtToLocalTime(occurredAt);

  // SMTDailyLogEntry returns an array of images, and the api/database support this behavior as well
  // However, for the foreseeable future only one image is supported per SMTEntry

  // photoContentType is used so the web build can detect & handle HEIC images (not supported on non-Safari browsers)
  const [photoUrl, thumbnailUrl, photoContentType] = isNotNilNotEmpty(images)
    ? [images[0].url, images[0].thumbnailUrl, images[0].contentType]
    : [undefined, undefined, undefined];

  return {
    avatarUserId,
    avatarUserName,
    avatarUserType,
    details: {
      entryDetailsProps:
        mapSMTDailyLogEntryToSMTEntryDetailProps(smtDailyLogEntry),
      entryType,
    },
    photoContentType,
    photoUrl,
    smtEntryId,
    thumbnailUrl,
    time,
  };
};

const placeholderLog = (dateString: string): SMTDailyLog => ({
  id: `empty-${dateString}`,
  logDate: dateString,
  smtEntries: [],
});

/**
 * Fills in missing dates in daily logs data with empty log entries.
 * For any date in the range that doesn't have a log, creates an empty DailyLog object.
 * @param params - Object containing dailyLogs, startDate, and endDate
 * @param params.dailyLogs - Array of daily logs from the API (may have gaps)
 * @param params.startDate - Start date in YYYY-MM-DD format
 * @param params.endDate - End date in YYYY-MM-DD format
 * @returns Complete array of daily logs with empty entries for missing dates, sorted by date (newest first)
 */
const populateMissingDailyLogs = ({
  dailyLogs,
  endDate,
  startDate,
}: {
  dailyLogs: SMTDailyLog[] | undefined;
  endDate: string;
  startDate: string;
}): SMTDailyLog[] => {
  const normalizedDailyLogs = dailyLogs ?? [];

  // Create a map of existing logs by date for quick lookup
  const logsMap = new Map<string, SMTDailyLog>();
  normalizedDailyLogs.forEach((log) => {
    logsMap.set(log.logDate, log);
  });

  // Parse date strings
  const start = parse(startDate, DateFormat.yearMonthDay, new Date());
  const end = parse(endDate, DateFormat.yearMonthDay, new Date());

  return eachDayOfInterval({ end, start })
    .map((date) => format(date, DateFormat.yearMonthDay))
    .map((day) => logsMap.get(day) ?? placeholderLog(day))
    .reverse();
};

/**
 * Finds and updates a specific daily log entry in a page array.
 * Returns a new array with the updated entry, maintaining immutability.
 *
 * @param params - The parameters object
 * @param params.freshLogEntry - The updated log entry to insert
 * @param params.logDate - The date of the log to update (YYYY-MM-DD format)
 * @param params.page - The array of daily logs to search in
 * @returns New array with the updated log entry, or the original array if not found
 */
const findAndUpdateDailyLogInPage = ({
  freshLogEntry,
  logDate,
  page,
}: {
  freshLogEntry: SMTDailyLog;
  logDate: string;
  page: SMTDailyLog[];
}): SMTDailyLog[] => {
  const index = page.findIndex((log) => log.logDate === logDate);
  return index === -1
    ? page
    : // eslint-disable-next-line max-params
      page.map((log, logIndex) => (logIndex === index ? freshLogEntry : log));
};

const mapSMTDailyLogsResponseToAccordionPropsList = (
  dailyLogs: SMTDailyLog[],
): AllSMTDailyEntriesModalProps["accordionPropsList"] => {
  return dailyLogs.map((dailyLog: SMTDailyLog) => ({
    dailyLogId: dailyLog.id,
    date: parse(dailyLog.logDate, DateFormat.yearMonthDay, new Date()),
    entries: (dailyLog.smtEntries ?? []).map(
      mapSMTDailyLogEntryToSMTEntryProps,
    ),
  }));
};

enum SMTWeekLogStreakState {
  Active = "active",
  KeepStreak = "keep_streak",
  None = "none",
  StartStreak = "start_streak",
}

const SMTWeekLogStreakDescription: Record<
  SMTWeekLogStreakState,
  string | undefined
> = {
  [SMTWeekLogStreakState.Active]: undefined,
  [SMTWeekLogStreakState.KeepStreak]: undefined,
  [SMTWeekLogStreakState.None]:
    "Use the plus button in the bottom corner of the screen to share what you've been eating. This information helps you and your provider team stay on top of your treatment.",
  [SMTWeekLogStreakState.StartStreak]:
    "Consistently sharing your activity helps you and your provider team stay on top of your treatment.",
};

const SMTWeekLogStreakLabel: Record<
  SMTWeekLogStreakState,
  ({ streakLength }: { streakLength: number }) => string
> = {
  [SMTWeekLogStreakState.Active]: ({
    streakLength,
  }: {
    streakLength: number;
  }) => `You're on a ${streakLength} day streak!`,
  [SMTWeekLogStreakState.KeepStreak]: () => "Add now to keep your streak",
  [SMTWeekLogStreakState.None]: () => "Add your first entry today!",
  [SMTWeekLogStreakState.StartStreak]: () => "Add an entry to start a streak",
};

/**
 * Determines the current streak state based on the global streak count and week day logs.
 *
 * @param params - The parameters object
 * @param params.globalStreak - The current global streak count (consecutive days including today)
 * @param params.weekDays - Array of week days with their log status, ordered from oldest to newest
 *
 * @returns The streak state:
 * - `Active`: User has a current active streak (globalStreak > 0 or has logged today)
 * - `KeepStreak`: User's most recent log was yesterday and can maintain streak by logging today
 * - `StartStreak`: User's most recent log was before yesterday, must start a new streak
 * - `None`: User has no logs at all
 *
 * @remarks
 * The function evaluates streak state in the following priority order:
 * 1. If globalStreak > 0, returns Active
 * 2. If no logs exist, returns None
 * 3. If most recent log is today, returns Active
 * 4. If most recent log is yesterday, returns KeepStreak
 * 5. Otherwise, returns StartStreak ( most recent log before yesterday )
 */
const getStreakStateFromStreaks = ({
  globalStreak,
  weekDays,
}: {
  globalStreak: number;
  weekDays: SMTWeekDay[];
}): SMTWeekLogStreakState => {
  if (globalStreak > 0) {
    return SMTWeekLogStreakState.Active;
  }

  // Global streak only counts consecutive days INCLUDING today
  const today = new Date();
  const yesterday = subDays(today, 1);

  // Find the most recent day with logs (regardless of streak status)
  const daysWithLogs = weekDays.filter((day) => day.hasLogs);

  // No entries at all
  if (isEmpty(daysWithLogs)) {
    return SMTWeekLogStreakState.None;
  }

  // Get the most recent entry (weekDays is ordered from oldest to newest)
  // eslint-disable-next-line unicorn/prefer-at -- at loses typing information
  const mostRecentEntry = daysWithLogs[daysWithLogs.length - 1];

  // Defensive coding - should have returned in globalStreak > 0 check above
  if (isSameDay(mostRecentEntry.date, today)) {
    // User has an active global streak (includes today)
    return SMTWeekLogStreakState.Active;
  }

  // If most recent entry is yesterday
  if (isSameDay(mostRecentEntry.date, yesterday)) {
    // Single entry on yesterday - user can keep streak going by adding today
    return SMTWeekLogStreakState.KeepStreak;
  }

  // Most recent entry is before yesterday - Can't keep a streak going
  return SMTWeekLogStreakState.StartStreak;
};

type AccordionPropsList = AllSMTDailyEntriesModalProps["accordionPropsList"];

interface SMTDailyLogsQueryResult {
  data?: {
    pages: SMTDailyLog[][];
  };
}

const MappedPageCache = new WeakMap<SMTDailyLog[], AccordionPropsList>();

const selectAccordionPropsList = createSelector(
  [(result: SMTDailyLogsQueryResult) => result.data?.pages],
  (pages): AccordionPropsList => {
    if (!pages) return [];

    return pages.flatMap((page) => {
      const cachedPage = MappedPageCache.get(page);
      if (cachedPage) return cachedPage;

      const mappedPage = mapSMTDailyLogsResponseToAccordionPropsList(page);
      MappedPageCache.set(page, mappedPage);
      return mappedPage;
    });
  },
);

export {
  AllEntriesStartDateOffset,
  canAddMeals,
  findAndUpdateDailyLogInPage,
  formatOccurredAtToLocalTime,
  generateWeekData,
  generateWeekDays,
  getDateRangeForWeekSummary,
  getInitialDateRangeForAllSMTDailyLogs,
  getNextDateRangeForAllSMTDailyLogs,
  getStreakStateFromStreaks,
  getUserInteractionStorageKey,
  hasLogForDate,
  identifyStreaks,
  isViewable,
  mapSMTDailyLogEntryToSMTEntryDetailProps,
  mapSMTDailyLogEntryToSMTEntryProps,
  mapSMTDailyLogsResponseToAccordionPropsList,
  mapSMTSummaryResponseToDates,
  parseDailyLogDate,
  populateMissingDailyLogs,
  selectAccordionPropsList,
  SMTWeekLogStreakDescription,
  SMTWeekLogStreakLabel,
  SMTWeekLogStreakState,
};
